let initialized = false
let currentMeasurementId = null

/** Page views requested before gtag exists (e.g. config not loaded yet). */
const pendingPageViews = []
const MAX_PENDING_PAGE_VIEWS = 20
const PAGE_VIEW_DEDUPE_WINDOW_MS = 5000
let currentViewItemListPage = ''
const sentViewItemListKeys = new Set()

// Dedupe helpers: router navigations can sometimes trigger GA tracking more
// than once for the same path. We ensure `page_view` fires only once per
// unique page_path (including query string).
const inFlightPageViewKeys = new Set()
let lastSentPageViewKey = null
let lastSentPageViewAt = 0

const markPageViewSent = (pageViewKey) => {
  inFlightPageViewKeys.delete(pageViewKey)
  lastSentPageViewKey = pageViewKey
  lastSentPageViewAt = Date.now()
}

const dispatchPageView = (payload, pageViewKey) => {
  const win = getWindow()
  if (!win) return
  const gtag = getGtag()
  if (!gtag) return

  const merged = {
    path: payload.path || win.location?.pathname || '',
    routeName: payload.routeName || '',
    title: payload.title ?? '',
  }

  // Always send immediately so route tracking does not depend on title mutation.
  sendPageView(gtag, win, {
    ...merged,
    title: merged.title || win.document?.title || merged.routeName,
  })
  markPageViewSent(pageViewKey)
}

/**
 * Send queued page views for *other* routes only. Drops pending entries for
 * `currentPath` so the current navigation is tracked exactly once via trackPageView().
 */
const flushPendingPageViewsExcept = (currentPath) => {
  const gtag = getGtag()
  if (!gtag || !pendingPageViews.length) return

  const skip = currentPath || ''
  const toFlush = []
  for (const p of pendingPageViews) {
    if ((p.path || '') === skip) {
      continue
    }
    toFlush.push(p)
  }
  pendingPageViews.length = 0
  toFlush.forEach((p) => {
    const key = `${currentMeasurementId || ''}|${p.path || ''}`
    dispatchPageView(p, key)
  })
}

const getWindow = () => {
  return typeof window === 'undefined' ? null : window
}

const getCurrentPathWithQuery = (win) => {
  if (!win?.location) return ''
  const pathname = win.location.pathname || ''
  const search = win.location.search || ''
  return `${pathname}${search}`
}

const getScriptSrc = (measurementId) =>
  `https://www.googletagmanager.com/gtag/js?id=${measurementId}`

const ensureScript = (measurementId) => {
  const win = getWindow()
  if (!win || !measurementId) return

  const existing = document.querySelector(
    'script[src^="https://www.googletagmanager.com/gtag/js"]'
  )
  if (existing) return

  const script = document.createElement('script')
  script.async = true
  script.src = getScriptSrc(measurementId)
  document.head.appendChild(script)
}

/**
 * Resolve GA4 measurement ID from `businessConfigurations.gaTrackingCode`.
 * @param {Record<string, unknown>} [businessConfigurations]
 * @returns {string|null}
 */
export const resolveGAMeasurementId = (businessConfigurations) => {
  const id = businessConfigurations?.gaTrackingCode
  return typeof id === 'string' && id.trim() ? id.trim() : null
}

/**
 * Initialize GA4. Call after configuration is loaded.
 * @param {string} measurementId - GA4 measurement ID (e.g. G-XXXXXXXX) from
 *   `businessConfigurations.gaTrackingCode`.
 */
export const initGA = (measurementId) => {
  const win = getWindow()
  if (!win) return

  const id = measurementId
  if (!id) return
  if (initialized && currentMeasurementId === id) {
    return
  }

  ensureScript(id)

  win.dataLayer = win.dataLayer || []
  function gtag() {
    win.dataLayer.push(arguments)
  }

  if (typeof win.gtag !== 'function') {
    win.gtag = gtag
  }

  win.gtag('js', new Date())
  win.gtag('config', id, {
    send_page_view: false,
  })

  initialized = true
  currentMeasurementId = id
}

const getGtag = () => {
  const win = getWindow()
  if (!win) return null
  if (typeof win.gtag !== 'function') return null
  return win.gtag
}

export const setGAUserId = (userId) => {
  const win = getWindow()
  if (!win || !userId) return
  const gtag = getGtag()
  if (!gtag) return

  gtag('set', {
    user_id: String(userId),
  })
}

const buildItem = (rawItem = {}, extra = {}) => {
  if (!rawItem) return null

  const base = {
    item_id:
      rawItem.productID ||
      rawItem.productId ||
      rawItem.id ||
      rawItem.sku ||
      rawItem.seoName ||
      undefined,
    sku: rawItem.seoName || rawItem.sku ||
      rawItem.productSku ||
      rawItem.sku ||
      undefined,
    item_name:
      rawItem.title ||
      rawItem.productTittle ||
      rawItem.name ||
      rawItem.seoName ||
      undefined,
    price:
      extra.price ??
      rawItem.price ??
      0,
    compareAtPrice: rawItem.compareAtPrice ?? 0,
  }

  return {
    ...base,
    ...extra,
  }
}

const defaultCurrency = 'USD'

const sendPageView = (gtag, win, { path, title, routeName }) => {
  const pageTitle = title ?? win.document?.title ?? routeName ?? ''
  const pagePath = path || win.location?.pathname || ''
  gtag('event', 'ftx_page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  })
}

const PAGE_VIEW_TITLE_WAIT_MS = 2500

/**
 * When no title is passed, wait for document.title to update (e.g. after
 * createMetaMixin sets it) then send page_view. Sends at most once; falls back
 * after PAGE_VIEW_TITLE_WAIT_MS if title never changes.
 */
const sendPageViewWhenTitleReady = (gtag, win, payload, onSent) => {
  let sent = false
  let observer = null

  const send = () => {
    if (sent) return
    sent = true
    if (observer) {
      observer.disconnect()
      observer = null
    }
    sendPageView(gtag, win, {
      ...payload,
      title: win.document?.title || payload.routeName,
    })
    onSent && onSent()
  }

  const titleEl = win.document.querySelector('title')
  if (!titleEl) {
    send()
    return
  }

  observer = new MutationObserver(() => {
    send()
  })
  observer.observe(titleEl, {
    childList: true,
    characterData: true,
    subtree: true,
  })

  win.setTimeout(() => {
    if (observer) observer.disconnect()
    send()
  }, PAGE_VIEW_TITLE_WAIT_MS)
}

export const trackPageView = ({ path, title, routeName } = {}) => {
  const win = getWindow()
  if (!win) return
  const payload = {
    path: path || win.location?.pathname || '',
    routeName: routeName || '',
    title: title ?? '',
  }

  const gtag = getGtag()
  if (!gtag) {
    // Queue only once per path while GA isn't ready yet.
    for (let i = pendingPageViews.length - 1; i >= 0; i--) {
      if ((pendingPageViews[i].path || '') === payload.path) {
        pendingPageViews.splice(i, 1)
      }
    }
    if (pendingPageViews.length < MAX_PENDING_PAGE_VIEWS) {
      pendingPageViews.push(payload)
    }
    return
  }

  const pageViewKey = `${currentMeasurementId || ''}|${payload.path || ''}`
  const now = Date.now()

  // Prevent duplicate `page_view` calls for the same page caused by
  // double router navigations or repeated boot initialization.
  if (
    (lastSentPageViewKey === pageViewKey &&
      now - lastSentPageViewAt < PAGE_VIEW_DEDUPE_WINDOW_MS) ||
    inFlightPageViewKeys.has(pageViewKey)
  ) {
    return
  }

  inFlightPageViewKeys.add(pageViewKey)
  // Safety valve: if the page unloads before title changes, we don't want the
  // in-flight key to block future navigations.
  win.setTimeout(() => {
    inFlightPageViewKeys.delete(pageViewKey)
  }, PAGE_VIEW_DEDUPE_WINDOW_MS + 3000)

  // Flush older queued routes only; drop same-path pending so we send this view once.
  flushPendingPageViewsExcept(payload.path)
  dispatchPageView(payload, pageViewKey)
}

/**
 * One entry point per navigation: ensure GA is initialized, then record exactly one page_view.
 * Prefer this from `router.afterEach` instead of calling `initGA` + `trackPageView` separately.
 */
export const trackRoutePageView = (
  measurementId,
  { path, title, routeName } = {}
) => {
  initGA(measurementId)
  trackPageView({ path, title, routeName })
}

export const trackViewItemList = ({ items = [], listId, listName } = {}) => {
  if (!items.length) return
  const win = getWindow()
  if (!win) return
  const gtag = getGtag()
  if (!gtag) return

  const pageKey = getCurrentPathWithQuery(win)
  if (pageKey !== currentViewItemListPage) {
    currentViewItemListPage = pageKey
    sentViewItemListKeys.clear()
  }

  const listKey = `${currentMeasurementId || ''}|${pageKey}|${listId || ''}|${listName || ''}`
  if (sentViewItemListKeys.has(listKey)) return

  const payloadItems = items
    .map((item, index) => buildItem(item, { index }))
    .filter(Boolean)

  if (!payloadItems.length) return

  sentViewItemListKeys.add(listKey)
  gtag('event', 'view_item_list', {
    item_list_id: listId,
    item_list_name: listName,
    items: payloadItems,
  })
}

export const trackViewItem = ({ item } = {}) => {
  if (!item) return
  const gtag = getGtag()
  if (!gtag) return

  const built = buildItem(item)
  if (!built) return

  gtag('event', 'view_item', {
    currency: defaultCurrency,
    value: built.price || 0,
    items: [built],
  })
}

export const trackAddToCart = ({ item, quantity } = {}) => {
  if (!item) return
  const gtag = getGtag()
  if (!gtag) return

  const built = buildItem(item, { quantity })
  if (!built) return

  gtag('event', 'add_to_cart', {
    currency: defaultCurrency,
    value: (built.price || 0) * (built.quantity || 1),
    items: [built],
  })
}

export const trackRemoveFromCart = ({ item, quantity } = {}) => {
  if (!item) return
  const gtag = getGtag()
  if (!gtag) return

  const built = buildItem(item, { quantity })
  if (!built) return

  gtag('event', 'remove_from_cart', {
    currency: defaultCurrency,
    value: (built.price || 0) * (built.quantity || 1),
    items: [built],
  })
}

export const trackViewCart = ({ items = [], value = 0 } = {}) => {
  if (!items.length) return
  const gtag = getGtag()
  if (!gtag) return

  const payloadItems = items.map((item) => buildItem(item)).filter(Boolean)
  if (!payloadItems.length) return

  gtag('event', 'view_cart', {
    currency: defaultCurrency,
    value,
    items: payloadItems,
  })
}

export const trackBeginCheckout = ({ items = [], value = 0 } = {}) => {
  if (!items.length) return
  const gtag = getGtag()
  if (!gtag) return

  const payloadItems = items.map((item) => buildItem(item)).filter(Boolean)
  if (!payloadItems.length) return

  gtag('event', 'begin_checkout', {
    currency: defaultCurrency,
    value,
    items: payloadItems,
  })
}

export const trackAddShippingInfo = ({
  items = [],
  value = 0,
  shippingTier,
} = {}) => {
  if (!items.length) return
  const gtag = getGtag()
  if (!gtag) return

  const payloadItems = items.map((item) => buildItem(item)).filter(Boolean)
  if (!payloadItems.length) return

  gtag('event', 'add_shipping_info', {
    currency: defaultCurrency,
    value,
    shipping_tier: shippingTier,
    items: payloadItems,
  })
}

export const trackAddPaymentInfo = ({
  items = [],
  value = 0,
  paymentType,
} = {}) => {
  if (!items.length) return
  const gtag = getGtag()
  if (!gtag) return

  const payloadItems = items.map((item) => buildItem(item)).filter(Boolean)
  if (!payloadItems.length) return

  gtag('event', 'add_payment_info', {
    currency: defaultCurrency,
    value,
    payment_type: paymentType,
    items: payloadItems,
  })
}

export const trackPurchase = ({
  transactionId,
  items = [],
  value = 0,
  tax = 0,
  shipping = 0,
  coupon,
} = {}) => {
  if (!transactionId || !items.length) return
  const gtag = getGtag()
  if (!gtag) return

  const payloadItems = items.map((item) => buildItem(item)).filter(Boolean)
  if (!payloadItems.length) return

  gtag('event', 'purchase', {
    transaction_id: transactionId,
    currency: defaultCurrency,
    value,
    tax,
    shipping,
    coupon,
    items: payloadItems,
  })
}
